// Copyright (c) 2016 Deere & Company

import {buildDate} from '../../../shared/services/build-date';
import {buildReportCurrency} from '../../../shared/services/build-number';

const isForceStringField = columnId => [
    'account',
    'accountNumber',
    'currentDealer',
    'currentDealerId',
    'dealer',
    'dealerAccount',
    'dealerInvoiceNumber',
    'invoiceDealer',
    'invoiceRequest',
    'leadAccount',
    'legacyCustomer',
    'newDealer',
    'responsibleDealer'
].some(field => field === columnId);

const isCurrencyField = columnId => [
    'approvedAmount',
    'approvedCoreValue',
    'approvedDiagnosticAmount',
    'approvedDiagnosticHours',
    'approvedLaborAdditive',
    'approvedLaborAmount',
    'approvedLaborHours',
    'approvedLaborTotal',
    'approvedOnshelfCost',
    'approvedOtherCredits',
    'approvedParts',
    'approvedPartsAdditive',
    'approvedPartsTotal',
    'approvedRepairAmount',
    'approvedRepairHours',
    'approvedTaxesTotal',
    'approvedTravelTotal',
    'deductible',
    'netAmount',
    'pipsUnpaidLaborAdditives',
    'reimbursedAmount',
    'requesteLaborAmount',
    'requestedAmount',
    'requestDiagnosticAmount',
    'requestDiagnosticHours',
    'requestedLaborAmount',
    'requestedLaborHours',
    'requestedOtherCredits',
    'requestedParts',
    'requestRepairAmount',
    'requestRepairHours',
    'requestedTravelTotal',
    'totalApproved',
    'totalApprovedClaimValue',
    'totalApprovedIncludingTaxes',
    'totalApprovedWithoutAdditives',
    'totalRequested',
    'totalRequestedClaimValue',
    'totalUnpaid',
    'unpaidLabor',
    'unpaidLaborAdditiveExcludingPips',
    'unpaidLaborAdditivesExcludingPips',
    'unpaidLaborAdditivesPips',
    'unpaidOtherCredits',
    'unpaidParts',
    'unpaidPartsAdditive',
    'unpaidTravel',
    'withAdditives'
].some(numberField => numberField === columnId);

const isDateField = columnId => [
    'actualAuditDate',
    'approvalDate',
    'basicWarrantyEndDate',
    'basicWarrantyStartDate',
    'beginDate',
    'contractPurchaseDate',
    'completionDate',
    'changedOn',
    'createdDate',
    'creationDate',
    'creditMemoDate',
    'dateIssued',
    'deliveryDate',
    'decisionDate',
    'effectiveDate',
    'endDate',
    'estimatedBasicDate',
    'expectedDateOfFirstUse',
    'expirationDate',
    'expireDate',
    'failureDate',
    'intervalBeginDate',
    'intervalEndDate',
    'invoiceDate',
    'originalSubmissionDate',
    'planEndDate',
    'planStartDate',
    'registrationDate',
    'repairDate',
    'reportDate',
    'requestDate',
    'settlementDate',
    'startDate',
    'submissionDate',
    'terminationDate',
    'updatedDate'
].some(dateField => dateField === columnId);

export const hasReport = numberOfReports => numberOfReports > 0;

export const getAttributesList = (itemList, attribute) =>
    itemList.map(item => item[attribute]);

export const putProp = (toObj, toProp, value) => value ? {...toObj, [toProp]: value} : toObj;

export const getAuditSummaryReportCriteria = criteria => {

    let reportCriteria;

    const territories = criteria.territories.map(dealer => dealer.territory);

    reportCriteria = {
        territories
    };

    reportCriteria = putProp(reportCriteria, 'fiscalYear', criteria.fiscalYear.toString());

    return reportCriteria;

};

export const getClaimHistoryReportCriteria = reportState => {

    let reportCriteria;

    reportCriteria = {
        pin: reportState.criteria.pin
    };

    reportCriteria = putProp(reportCriteria, 'creditMemoDateFrom', reportState.criteria.creditMemoProcessDate.from);
    reportCriteria = putProp(reportCriteria, 'creditMemoDateTo', reportState.criteria.creditMemoProcessDate.to);
    reportCriteria = putProp(reportCriteria, 'claimType', reportState.criteria.claimType.key);
    reportCriteria = putProp(reportCriteria, 'account', reportState.criteria.selectedDealer.account);
    reportCriteria = putProp(reportCriteria, 'reference1', reportState.criteria.workOrderOne);
    reportCriteria = putProp(reportCriteria, 'reference2', reportState.criteria.workOrderTwo);

    return reportCriteria;

};

export const getCreditMemoReportCriteria = reportState => {

    let reportCriteria;

    reportCriteria = {};

    reportCriteria = putProp(reportCriteria, 'pin', reportState.criteria.pin);
    reportCriteria = putProp(reportCriteria, 'claimNumber', reportState.criteria.claimNumber);
    reportCriteria = putProp(reportCriteria, 'dateIssuedFrom', reportState.criteria.dateIssued.from);
    reportCriteria = putProp(reportCriteria, 'dateIssuesTo', reportState.criteria.dateIssued.to);
    reportCriteria = putProp(reportCriteria, 'claimType', reportState.criteria.claimTypes.selected);
    reportCriteria = putProp(reportCriteria, 'dealers', reportState.selectedDealers);
    reportCriteria = putProp(reportCriteria, 'referenceNumber1', reportState.criteria.workOrderOne);
    reportCriteria = putProp(reportCriteria, 'creditMemoNumber', reportState.criteria.creditMemo);
    reportCriteria = putProp(reportCriteria, 'invoiceRequest', reportState.criteria.invoiceRequest);

    return reportCriteria;

};

export const getDemoRentalReportCriteria = criteria => {

    let reportCriteria;

    reportCriteria = {
        branch: criteria.branch,
        dealers: criteria.dealers,
        factoryCode: criteria.factoryCode,
        pin: criteria.pin,
        productRegistered: criteria.productRegistered,
        status: criteria.status
    };

    reportCriteria = putProp(reportCriteria, 'beginDateFrom', criteria.beginDate.from);
    reportCriteria = putProp(reportCriteria, 'beginDateTo', criteria.beginDate.to);
    reportCriteria = putProp(reportCriteria, 'endDateTo', criteria.endDate.to);
    reportCriteria = putProp(reportCriteria, 'endDateFrom', criteria.endDate.from);

    return reportCriteria;

};

export const getMaintenancePlanReportCriteria = criteria => {

    const selectedServiceIntervalStatuses = getAttributesList(criteria.serviceIntervalStatuses.selected, 'key');

    let reportCriteria;

    reportCriteria = {
        dealers: criteria.selectedDealers,
        model: criteria.model,
        pin: criteria.pin,
        planName: criteria.planName,
        serviceIntervalEndDateFrom: criteria.serviceIntervalEndDate.from,
        serviceIntervalEndDateTo: criteria.serviceIntervalEndDate.to,
        serviceIntervalStatus: selectedServiceIntervalStatuses,
        serviceIntervalType: criteria.serviceIntervalType
    };

    reportCriteria = putProp(reportCriteria, 'planEndDateFrom', criteria.planEndDate.from);
    reportCriteria = putProp(reportCriteria, 'planEndDateTo', criteria.planEndDate.to);
    reportCriteria = putProp(reportCriteria, 'planStartDateFrom', criteria.planStartDate.from);
    reportCriteria = putProp(reportCriteria, 'planStartDateTo', criteria.planStartDate.to);
    reportCriteria = putProp(reportCriteria, 'serviceIntervalBeginDateFrom', criteria.serviceIntervalBeginDate.from);
    reportCriteria = putProp(reportCriteria, 'serviceIntervalBeginDateTo', criteria.serviceIntervalBeginDate.to);

    return reportCriteria;

};

export const getRegistrationsReviewReportCriteria = criteria => {

    let reportCriteria;

    reportCriteria = {
        dealers: criteria.selectedDealers,
        registrationStatuses: criteria.registrationStatus.selected
    };

    reportCriteria = putProp(reportCriteria, 'deliveryDateFrom', criteria.deliveryDate.from);
    reportCriteria = putProp(reportCriteria, 'deliveryDateTo', criteria.deliveryDate.to);
    reportCriteria = putProp(reportCriteria, 'registrationDateFrom', criteria.registrationDate.from);
    reportCriteria = putProp(reportCriteria, 'registrationDateTo', criteria.registrationDate.to);

    return reportCriteria;

};

const extractListOfTerritories = list => list.map(item => item.territory);

export const getSpecialAllowanceClaimStatusReportCriteria = criteria => {

    let reportCriteria;

    reportCriteria = {
        territories: extractListOfTerritories(criteria.selectedTerritories)
    };

    reportCriteria = putProp(reportCriteria, 'originalSubmissionDateFrom', criteria.originalSubmissionDate.from);
    reportCriteria = putProp(reportCriteria, 'originalSubmissionDateTo', criteria.originalSubmissionDate.to);

    return reportCriteria;

};

/* eslint-disable complexity */

export const getFormattedCellValues = params => {

    let returnString;

    const columnId = params.column.colId;

    if (isCurrencyField(columnId)) {

        returnString = buildReportCurrency(params.value);

    } else if (params.value === null || params.value === '') {

        returnString = ' ';

    } else if (isForceStringField(columnId)) {

        returnString = `${params.value}${String.fromCharCode(9)}`;

    } else if (isDateField(columnId)) {

        returnString = buildDate(params.value);

    } else {

        returnString = params.value;

    }

    return returnString;

};

/* eslint-enable complexity */



// WEBPACK FOOTER //
// ./src/reports/shared/services/reports.js